/**
 * The ahwa table — fullscreen game orchestrator.
 *
 * Responsibilities:
 * - Round-opening ceremony: shuffle rattle, then tiles dealt seat-by-seat.
 * - Drag/tap tile input with drop-target snapping on the felt.
 * - Physics-driven placement (flights, landings, slams) via <Board/>.
 * - Round-end callouts synced to the action.
 * - Tea-glass scoreboard, turn toasts, round-over & match-over overlays.
 * - Landscape-first scaling for desktop and mobile.
 */

import type { End, GameMode, PlayerView } from "@domino/types";
import { DOUBLE_NAMES, tileName } from "../lib/slang";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { prefersReducedMotion, tableShake } from "../lib/physics";
import { haptic, sounds } from "../lib/sound";
import { Board, type BoardFlight, type FlightDir } from "./board";
import { displayName } from "../lib/player";
import { TileBack, TileFace } from "./tiles";

export interface SeatInfo {
  seatIndex: number;
  name: string;
  team: number | null;
  count: number;
  isTurn: boolean;
  isMe: boolean;
  connected?: boolean;
}

export interface GameTableProps {
  view: PlayerView;
  mode: GameMode;
  /** Felt theme chosen by the room owner: green | red | sand | night. */
  boardStyle?: string;
  seats: SeatInfo[];
  scores?: number[];
  targetScore?: number;
  onPlay: (tileId: number, end?: End) => void;
  onDraw?: () => void;
  onPass?: () => void;
  onNextRound?: () => void;
  canNextRound?: boolean;
  title?: string;
  /** True when the current seat has been idle past the stall threshold. */
  turnStale?: boolean;
  onSkipStale?: () => void;
  footer?: ReactNode;
  statusNote?: ReactNode;
  onLeave?: () => void;
}

type Phase = "dealing" | "playing";

interface DragState {
  tileId: number;
  x: number;
  y: number;
  startX: number;
  startY: number;
  isDragging: boolean;
}

interface Callout {
  id: number;
  ar: string;
  en: string;
  sub?: string;
  tone: "slam" | "win" | "block";
}

const TEAM_HUES = ["t1", "t2"] as const;

export function GameTable({
  view,
  boardStyle,
  turnStale,
  onSkipStale,
  mode,
  seats,
  scores,
  targetScore,
  onPlay,
  onDraw,
  onPass,
  onNextRound,
  canNextRound = false,
  title,
  footer,
  statusNote,
  onLeave,
}: GameTableProps) {
  const [selected, setSelected] = useState<number | null>(null);
  const [soundOn, setSoundOn] = useState(sounds.enabled);
  const [dragState, setDragState] = useState<DragState | null>(null);
  const [dragOverEnd, setDragOverEnd] = useState<End | null>(null);
  const [phase, setPhase] = useState<Phase>("dealing");
  const [flight, setFlight] = useState<BoardFlight | null>(null);
  const [callout, setCallout] = useState<Callout | null>(null);
  const [turnToast, setTurnToast] = useState<string | null>(null);
  const [dealRoundKey, setDealRoundKey] = useState(0);
  const [guardOff, setGuardOff] = useState(
    () => typeof window !== "undefined" && window.localStorage.getItem("domino.guardOff") === "1",
  );

  const feltRef = useRef<HTMLDivElement | null>(null);
  const calloutSeq = useRef(0);
  const lastTurnSeat = useRef<number | null>(null);
  const pendingLand = useRef<{ tileId: number; dir: FlightDir; isMine: boolean } | null>(null);
  const roundRef = useRef(view.roundNumber);
  const endAnnounced = useRef(false);

  const legalById = useMemo(
    () => new Map(view.legalMoves.map((move) => [move.tileId as number, move.ends as End[]])),
    [view.legalMoves],
  );
  const myTurn =
    view.status === "active" && phase === "playing" && view.currentSeat === view.mySeat;
  const opening = view.chain.placements.length === 0;
  const canDrawFromYard = myTurn && !!view.canDraw && view.boneyardCount > 0;
  const noMoves = myTurn && view.legalMoves.length === 0;

  // ---------------------------------------------------------------- layout
  const numSeats = seats.length || 4;
  const mySeatIdx = view.mySeat >= 0 ? view.mySeat : 0;
  const seatAtOffset = (offset: number): SeatInfo | undefined => {
    const idx = (mySeatIdx + offset) % numSeats;
    return seats.find((s) => s.seatIndex === idx);
  };
  const bottomSeat = seats.find((s) => s.seatIndex === mySeatIdx) ?? seats[0];
  const leftSeat = numSeats >= 3 ? seatAtOffset(1) : undefined;
  const topSeat = numSeats === 2 ? seatAtOffset(1) : numSeats >= 3 ? seatAtOffset(2) : undefined;
  const rightSeat = numSeats === 4 ? seatAtOffset(3) : undefined;

  /** Which screen direction does a seat play from? */
  const dirForSeat = useCallback(
    (seatIndex: number): FlightDir => {
      if (seatIndex === mySeatIdx) return "bottom";
      const offset = (seatIndex - mySeatIdx + numSeats) % numSeats;
      if (offset === 1) return numSeats === 2 ? "top" : "left";
      if (offset === 2) return "top";
      return "right";
    },
    [mySeatIdx, numSeats],
  );

  const activeTileId = dragState?.tileId ?? selected;
  const activeEnds: End[] =
    activeTileId !== null
      ? opening
        ? ["center"]
        : (legalById.get(activeTileId) ?? [])
      : [];

  // ------------------------------------------------------------- ceremony
  const startDeal = useCallback(() => {
    setPhase("dealing");
    setSelected(null);
    endAnnounced.current = false;
    if (!prefersReducedMotion()) sounds.shuffle(1250);
    // DealCeremony owns the timeline and calls onDone() -> playing.
  }, []);

  // Kick the ceremony on first mount and on every new round number.
  useEffect(() => {
    roundRef.current = view.roundNumber;
    const cleanup = startDeal();
    return cleanup;
  }, [view.roundNumber]);

  // Turn-change toast + ping (hot-seat aware).
  useEffect(() => {
    if (view.status !== "active" || phase !== "playing") return;
    if (lastTurnSeat.current === null) {
      lastTurnSeat.current = view.currentSeat;
      return;
    }
    if (lastTurnSeat.current !== view.currentSeat) {
      lastTurnSeat.current = view.currentSeat;
      const who = seats.find((s) => s.seatIndex === view.currentSeat);
      if (who) {
        setTurnToast(who.isMe ? "دورك! Your turn" : `${who.name}'s turn`);
        const t = setTimeout(() => setTurnToast(null), 1300);
        if (who.isMe) sounds.turnPing();
        return () => clearTimeout(t);
      }
    }
  }, [view.currentSeat, view.status, seats, phase]);

  // Round end: announce with sound + callout.
  useEffect(() => {
    if (view.status !== "ended" || endAnnounced.current) return;
    endAnnounced.current = true;
    const result = view.result;
    if (result?.reason === "block") {
      sounds.block();
      pushCallout({ ar: "اتقفلت", en: "Round over — blocked", tone: "block" });
    } else {
      sounds.fanfare();
      haptic(30);
      pushCallout({ ar: "Round over!", en: "Out of tiles — count the remaining hands", tone: "win" });
    }
  }, [view.status]);

  function pushCallout(c: Omit<Callout, "id">) {
    calloutSeq.current += 1;
    const id = calloutSeq.current;
    setCallout({ ...c, id });
    const ttl = c.tone === "slam" ? 950 : 1600;
    setTimeout(() => {
      setCallout((current) => (current && current.id === id ? null : current));
    }, ttl);
  }

  // ------------------------------------------------- event-driven effects
  // Plays (mine or opponents') fly in from the seat that played them;
  // draws flick from the yard; passes knock the table.
  const lastEventSeq = useRef(0);
  const eventRound = useRef(view.roundNumber);
  const eventInit = useRef(false);
  useEffect(() => {
    if (eventRound.current !== view.roundNumber) {
      eventRound.current = view.roundNumber;
      lastEventSeq.current = 0;
      eventInit.current = false;
    }
    if (!eventInit.current) {
      // First run (fresh mount / rejoin): swallow the existing log so we
      // don't replay every historical flight and sound.
      eventInit.current = true;
      lastEventSeq.current = view.events.reduce((max, ev) => Math.max(max, ev.seq), 0);
      return;
    }
    for (const ev of view.events) {
      if (ev.seq <= lastEventSeq.current) continue;
      lastEventSeq.current = ev.seq;
      if (ev.type === "play" && ev.tileId !== undefined) {
        const seat = ev.seat ?? mySeatIdx;
        pendingLand.current = { tileId: ev.tileId, dir: dirForSeat(seat), isMine: seat === mySeatIdx };
        setFlight({ tileId: ev.tileId, dir: dirForSeat(seat), nonce: Date.now() + ev.seq });
      } else if (ev.type === "draw") {
        sounds.draw(0.4);
      } else if (ev.type === "pass") {
        sounds.knock(-0.3);
      }
    }
  }, [view.events, view.roundNumber, mySeatIdx, dirForSeat]);

  // ------------------------------------------------------------- landings
  const handleLanded = useCallback(
    (tileId: number, isDouble: boolean) => {
      const pending = pendingLand.current;
      const isMine = pending?.tileId === tileId ? pending.isMine : false;
      const pan = 0;
      if (isDouble) {
        const placement = view.chain.placements.find((p) => p.tileId === tileId);
        const value = placement?.inwardHalf ?? 6;
        const name = DOUBLE_NAMES[value];
        sounds.slam(pan);
        haptic(24);
        if (feltRef.current) tableShake(feltRef.current, 1);
        if (name) pushCallout({ ar: `${name.ar}!`, en: `${name.en} — double ${value}·${value}`, tone: "slam" });
      } else {
        sounds.place(pan, isMine ? 1 : 0.8);
        if (isMine) haptic(10);
      }
      if (pendingLand.current?.tileId === tileId) pendingLand.current = null;
    },
    [view.chain.placements],
  );

  // --------------------------------------------------------------- input
  const handleDropTargetHit = useCallback((x: number, y: number, validEnds: End[]): End | null => {
    const targets = document.querySelectorAll<HTMLElement>("[data-drop-end]");
    let bestEnd: End | null = null;
    let bestDist = 150;
    targets.forEach((target) => {
      const endType = target.getAttribute("data-drop-end") as End;
      if (!endType || !validEnds.includes(endType)) return;
      const rect = target.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dist = Math.hypot(x - cx, y - cy);
      if (dist < bestDist) {
        bestDist = dist;
        bestEnd = endType;
      }
    });
    return bestEnd;
  }, []);

  useEffect(() => {
    if (!dragState) return;
    const tileId = dragState.tileId;

    function endsFor(id: number): End[] {
      return opening ? (["center"] as End[]) : (legalById.get(id) ?? []);
    }

    function onMove(e: PointerEvent) {
      const dx = e.clientX - (dragState?.startX ?? 0);
      const dy = e.clientY - (dragState?.startY ?? 0);
      const moved = Math.hypot(dx, dy) > 6;
      const over = handleDropTargetHit(e.clientX, e.clientY, endsFor(tileId));
      setDragOverEnd(over);
      setDragState((prev) =>
        prev ? { ...prev, x: e.clientX, y: e.clientY, isDragging: prev.isDragging || moved } : null,
      );
    }

    function onUp(e: PointerEvent) {
      const current = dragState;
      if (!current) return;
      if (current.isDragging) {
        const ends = endsFor(tileId);
        const target = handleDropTargetHit(e.clientX, e.clientY, ends);
        if (target && ends.includes(target)) {
          commitPlay(tileId, target);
          setDragState(null);
          setDragOverEnd(null);
          return;
        }
        sounds.pickup();
      } else {
        handleTileTap(tileId);
      }
      setDragState(null);
      setDragOverEnd(null);
    }

    function onCancel() {
      setDragState(null);
      setDragOverEnd(null);
    }

    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
    };
  }, [dragState, handleDropTargetHit, legalById, opening]);

  function commitPlay(tileId: number, end: End) {
    if (!myTurn) return;
    setSelected(null);
    onPlay(tileId, end); // the event effect turns this into a flight + landing
  }

  function handleTileTap(tileId: number) {
    if (!myTurn) return;
    const ends = legalById.get(tileId);
    if (!ends || ends.length === 0) return;
    if (opening || ends.length === 1) {
      commitPlay(tileId, opening ? "center" : ends[0]);
      return;
    }
    sounds.pickup();
    setSelected((prev) => (prev === tileId ? null : tileId));
  }

  function handleTilePointerDown(e: React.PointerEvent, tileId: number) {
    if (!myTurn) return;
    const ends = legalById.get(tileId);
    if (!ends || ends.length === 0) return;
    e.preventDefault();
    sounds.pickup();
    setDragState({
      tileId,
      x: e.clientX,
      y: e.clientY,
      startX: e.clientX,
      startY: e.clientY,
      isDragging: false,
    });
  }

  function handleChooseEnd(end: End) {
    if (!myTurn || activeTileId === null) return;
    commitPlay(activeTileId, end);
    setDragState(null);
  }

  function handleDraw() {
    if (!canDrawFromYard || !onDraw) return;
    sounds.draw();
    haptic(8);
    pendingLand.current = null;
    onDraw();
  }

  function handlePass() {
    if (!onPass || !view.canPass) return;
    sounds.knock();
    haptic(8);
    onPass();
  }

  function toggleSound() {
    const next = !soundOn;
    setSoundOn(next);
    sounds.setEnabled(next);
    if (next) sounds.ui();
  }

  // One scoreboard cell per scoring side (team in 2v2, seat otherwise).
  const scoreboardSides = (() => {
    const sideCount = mode === "2v2" ? 2 : seats.length;
    return Array.from({ length: sideCount }, (_, side) => {
      const members = seats.filter((seat) =>
        mode === "2v2" ? seat.team === side : seat.seatIndex === side,
      );
      const score = scores?.[side] ?? 0;
      const isTurn = members.some((m) => m.isTurn);
      const mine = members.some((m) => m.isMe);
      const hue = mode === "2v2" ? TEAM_HUES[side % 2] : side === 2 ? "t3" : TEAM_HUES[side % 2];
      return {
        side,
        label: mode === "2v2" ? `فريق ${side + 1}` : members[0]?.name ?? `P${side + 1}`,
        members: members.map((m) => ({ name: m.name, active: m.isTurn })),
        score,
        pct: targetScore ? Math.min(100, (score / targetScore) * 100) : 0,
        hot: targetScore ? score >= targetScore * 0.75 : false,
        isTurn,
        mine,
        hue,
      };
    });
  })();

  /** The current player also gets a pill in the top bar. */
  function renderMyChip() {
    const seat = seats.find((s) => s.isMe);
    if (!seat) return null;
    return (
      <span
        className={`opp-chip chip-me ${seat.isTurn ? "chip-turn" : ""} ${
          seat.connected === false ? "chip-off" : ""
        } ${seat.team !== null ? TEAM_HUES[seat.team] : ""}`}
        title="You"
      >
        <span className="chip-name">{seat.name}</span>
        <b className="chip-count">{seat.count}</b>
      </span>
    );
  }

  /** Opponent identity lives in the top bar: a tiny pill, never on the felt. */
  function renderOppChip(seat: SeatInfo) {
    return (
      <span
        data-seat={seat.seatIndex}
        className={`opp-chip ${seat.isTurn ? "chip-turn" : ""} ${
          seat.connected === false ? "chip-off" : ""
        } ${seat.team !== null ? TEAM_HUES[seat.team] : ""}`}
        title={`${seat.name} — ${seat.count} tiles left${seat.connected === false ? " (away)" : ""}`}
      >
        <span className="chip-name">{seat.name}</span>
        <b className="chip-count">{seat.count}</b>
      </span>
    );
  }

  return (
    <div
      className={`table-root board-${boardStyle ?? "green"} players-${numSeats}`}
      onPointerDown={() => sounds.unlock()}
    >
      {/* ---------------------------------------------------------- HUD */}
      <header className="table-hud">
        <div className="hud-side hud-left">
          {onLeave && (
            <button
              type="button"
              className="hud-btn hud-exit-btn"
              onClick={onLeave}
              title={title ?? "Leave"}
            >
              <span className="hud-btn-ar">↩</span> خروج
            </button>
          )}
          {renderMyChip()}
          {leftSeat !== undefined && renderOppChip(leftSeat)}
          <span className="hud-pill hud-round-pill">
            الدور <b>{view.roundNumber}</b>
          </span>
        </div>

        <div className="scoreboard">
          <div
            className={`team-panel team-a ${scoreboardSides[0]?.isTurn ? "panel-turn" : ""} ${
              scoreboardSides[0]?.hot ? "panel-hot" : ""
            } ${scoreboardSides[0]?.mine ? "panel-mine" : ""}`}
          >
            <div className="team-names">
              <span className="team-title">
                {scoreboardSides[0]?.label}
                {scoreboardSides[0]?.isTurn ? <i className="turn-flag">دورهم</i> : null}
              </span>
              {scoreboardSides[0]?.members.length > 1 &&
                scoreboardSides[0].members.map((m, i) => (
                  <span key={i} className={`team-member ${m.active ? "member-turn" : ""}`}>
                    {m.name}
                  </span>
                ))}
            </div>
            <span className="team-score">{scoreboardSides[0]?.score}</span>
          </div>

          <div className="sb-mid">
            <span className="sb-target-num">{targetScore}</span>
            <span className="sb-target-cap">أولها يخسر</span>
          </div>

          <div
            className={`team-panel team-b ${scoreboardSides[1]?.isTurn ? "panel-turn" : ""} ${
              scoreboardSides[1]?.hot ? "panel-hot" : ""
            } ${scoreboardSides[1]?.mine ? "panel-mine" : ""}`}
          >
            <span className="team-score">{scoreboardSides[1]?.score ?? ""}</span>
            <div className="team-names team-names-end">
              <span className="team-title">
                {scoreboardSides[1]?.isTurn ? <i className="turn-flag">دورهم</i> : null}
                {scoreboardSides[1]?.label}
              </span>
              {scoreboardSides[1]?.members.length > 1 &&
                scoreboardSides[1].members.map((m, i) => (
                  <span key={i} className={`team-member ${m.active ? "member-turn" : ""}`}>
                    {m.name}
                  </span>
                ))}
            </div>
          </div>
        </div>

        {/* compact top-center scoreboard (CSGO-style) — all screens */}
        <div className="scoreboard-notch">
          <span className={`notch-team ${scoreboardSides[0]?.isTurn ? "nt-turn" : ""}`}>
            {mode === "2v2" ? "Team 1" : scoreboardSides[0]?.label}{" "}
            <b>{scoreboardSides[0]?.score ?? 0}</b>
          </span>
          <span className="notch-mid">
            <span className="notch-target">{targetScore ?? "—"}</span>
            <span className="notch-cap">Round {view.roundNumber}</span>
          </span>
          <span className={`notch-team ${scoreboardSides[1]?.isTurn ? "nt-turn" : ""}`}>
            <b>{scoreboardSides[1]?.score ?? 0}</b>{" "}
            {mode === "2v2" ? "Team 2" : scoreboardSides[1]?.label}
          </span>
        </div>

        <div className="hud-side hud-right">
          {topSeat !== undefined && renderOppChip(topSeat)}
          {rightSeat !== undefined && renderOppChip(rightSeat)}
          <button
            type="button"
            className={`hud-btn hud-icon-btn ${soundOn ? "" : "hud-btn-off"}`}
            onClick={toggleSound}
            title={soundOn ? "Mute" : "Unmute"}
            aria-pressed={soundOn}
          >
            {soundOn ? "🔊" : "🔇"}
          </button>
        </div>
      </header>

      {/* --------------------------------------------------------- STAGE */}
      <main className="table-stage">
        <div className="table-room" aria-hidden>
          <span className="lamp lamp-left" />
          <span className="lamp lamp-right" />
          <span className="mashrabiya" />
        </div>

        <div className="table-felt" ref={feltRef}>
          <Board
            chain={view.chain}
            activeEnds={activeEnds}
            dragOverEnd={dragOverEnd}
            isDragging={!!dragState?.isDragging}
            onChooseEnd={handleChooseEnd}
            flight={flight}
            onLanded={handleLanded}
          />

        </div>

        {/* deal ceremony overlay */}
        {phase === "dealing" && (
          <DealCeremony
            key={dealRoundKey}
            seats={seats}
            roundNumber={view.roundNumber}
            onDone={() => {
              setPhase("playing");
              setDealRoundKey((k) => k + 1);
            }}
          />
        )}

        {/* slang callout */}
        {callout && (
          <div key={callout.id} className={`callout callout-${callout.tone}`} aria-live="polite">
            <span className="callout-ar">{callout.ar}</span>
            <span className="callout-en">{callout.en}</span>
          </div>
        )}

        {/* stalled-turn skip: any awake player may move the round along */}
        {turnStale && !myTurn && view.status === "active" && onSkipStale && (
          <button type="button" className="skip-stale-btn" onClick={onSkipStale}>
            المقعد فاضي — skip the asleep turn
          </button>
        )}

        {/* turn toast */}
        {turnToast && phase === "playing" && view.status === "active" && (
          <div className="turn-toast" key={turnToast}>
            {turnToast}
          </div>
        )}
      </main>

      {/* ---------------------------------------------------------- DOCK */}
      <footer className="table-dock">
        <div className="dock-me">
          <div className="dock-me-meta">
            <span className="dock-me-name">{bottomSeat?.name ?? "You"}</span>
            {bottomSeat?.team !== null && bottomSeat?.team !== undefined && (
              <span className={`team-chip ${TEAM_HUES[bottomSeat.team]}`}>
                فريق {bottomSeat.team + 1}
              </span>
            )}
          </div>
        </div>

        <div
          className="dock-hand"
          key={`hand-${dealRoundKey}`}
          style={
            view.myHand.length > 10
              ? ({ "--hand-tile-h": "calc(clamp(46px, 15.5vmin, 76px) * 0.7)" } as React.CSSProperties)
              : undefined
          }
        >
          {view.myHand.length === 0 ? (
            <span className="hand-empty">خلص الكف — no tiles left</span>
          ) : phase === "dealing" ? (
            <div className="hand-facedown">
              {view.myHand.map((_, i) => (
                <TileBack key={i} className="hand-back-tile" style={{ animationDelay: `${i * 60}ms` }} />
              ))}
            </div>
          ) : (
            view.myHand.map((tile, i) => {
              const ends = legalById.get(tile.id) ?? [];
              const playable = myTurn && ends.length > 0;
              const isBeingDragged = dragState?.tileId === tile.id;
              const isSelected = selected === tile.id;
              const slang = tileName(tile.high, tile.low);
              const name = `${slang.en} ${slang.ar} · ${tile.high}·${tile.low}`;
              return (
                <div
                  key={tile.id}
                  className={`hand-slot ${playable ? "slot-playable" : "slot-locked"}`}
                  onPointerDown={(e) => {
                    if (playable) handleTilePointerDown(e, tile.id);
                  }}
                  role="button"
                  tabIndex={playable ? 0 : -1}
                  onKeyDown={(e) => {
                    if (playable && (e.key === "Enter" || e.key === " ")) {
                      e.preventDefault();
                      handleTileTap(tile.id);
                    }
                  }}
                  aria-label={`${name}${playable ? " (playable)" : ""}`}
                >
                  <TileFace
                    tileId={tile.id}
                    dimmed={myTurn && !playable}
                    raised={playable && !isBeingDragged}
                    selected={isSelected}
                    isDragging={isBeingDragged}
                    size="lg"
                    className="hand-tile deal-flip"
                    style={{ animationDelay: `${i * 70}ms` }}
                    title={`${name} — drag to the board or tap`}
                  />
                </div>
              );
            })
          )}
        </div>

        <div className="dock-actions">
          {view.status === "ended" && onNextRound && !statusNote && (
            <button
              type="button"
              className="action-btn action-primary"
              onClick={() => {
                sounds.ui();
                onNextRound();
              }}
              disabled={!canNextRound}
            >
              Next round →
            </button>
          )}
          {view.status === "active" &&
            (myTurn ? (
              <div className="dock-actions-row">
                <span className={`turn-hint ${opening ? "turn-hint-open" : ""}`}>
                  {opening
                    ? "افتح بأعلى دبل — lead with the highest double"
                    : noMoves && canDrawFromYard
                      ? "معكش لعب — لازم تسحب الأول"
                      : "دورك — play · draw · pass"}
                </span>
                {canDrawFromYard && onDraw && (
                  <button
                    type="button"
                    className={`action-btn ${noMoves ? "action-gold" : "action-silver"}`}
                    onClick={handleDraw}
                    title="Draw from el-mashoob — free any time"
                  >
                    اسحب +1 ({view.boneyardCount})
                  </button>
                )}
                {view.canPass && onPass && (
                  <button
                    type="button"
                    className="action-btn action-silver"
                    onClick={handlePass}
                    title="قاعد — pass your turn"
                  >
                    قاعد · Pass
                  </button>
                )}
              </div>
            ) : (
              <span className="turn-wait">
                <span className="wait-dot" />
                مستني {seats.find((s) => s.isTurn)?.name ?? "…"}
              </span>
            ))}
        </div>
      </footer>

      {/* floating drag ghost */}
      {dragState && dragState.isDragging && (
        <div className="drag-ghost" style={{ left: dragState.x, top: dragState.y }}>
          <TileFace
            tileId={dragState.tileId}
            size="lg"
            isDragging
            title="Release on a glowing end"
          />
        </div>
      )}

      {/* round-over verdict (blocking) or lightweight status note */}
      {statusNote && view.status === "ended" && (
        <div className="table-status-layer">
          <div className="verdict-stack">
            {statusNote}
            {onNextRound && canNextRound && (
              <button
                type="button"
                className="action-btn action-primary verdict-next-btn"
                onClick={() => {
                  sounds.ui();
                  onNextRound();
                }}
              >
                Next round →
              </button>
            )}
          </div>
        </div>
      )}
      {statusNote && view.status !== "ended" && (
        <div className="table-note-layer">{statusNote}</div>
      )}

      {footer && <div className="table-chat-drawer">{footer}</div>}


      {/* portrait guard (touch devices; escapable) */}
      {!guardOff && (
        <div className="orientation-guard" aria-live="polite">
          <div className="orientation-card">
            <div className="orientation-phone">
              <span className="orientation-phone-body">📱</span>
              <span className="orientation-arrow">↻</span>
            </div>
            <h2>لفّ الموبايل</h2>
            <p>The ahwa table is widescreen — turn your phone sideways to sit down.</p>
            <button
              type="button"
              className="orientation-dismiss"
              onClick={() => {
                setGuardOff(true);
                try {
                  window.localStorage.setItem("domino.guardOff", "1");
                } catch {
                  // private mode
                }
              }}
            >
              العب عمودي برضه · play portrait anyway
            </button>
            <span className="orientation-pill">BEST IN LANDSCAPE</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Deal ceremony — shuffle + seat-by-seat distribution
// ---------------------------------------------------------------------------

function DealCeremony({
  seats,
  roundNumber,
  onDone,
}: {
  seats: SeatInfo[];
  roundNumber: number;
  onDone: () => void;
}) {
  const stageRef = useRef<HTMLDivElement | null>(null);
  const pileRef = useRef<HTMLDivElement | null>(null);
  const [step, setStep] = useState<"shuffle" | "deal">("shuffle");
  const flyingRef = useRef<HTMLDivElement | null>(null);
  const doneRef = useRef(false);

  const finish = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    onDone();
  }, [onDone]);

  useEffect(() => {
    const reduced = prefersReducedMotion();
    if (reduced) {
      const t = setTimeout(finish, 260);
      return () => clearTimeout(t);
    }
    const shuffleT = setTimeout(() => setStep("deal"), 1500);
    return () => clearTimeout(shuffleT);
  }, [finish]);

  // Deal flights from the pile to every seat.
  useEffect(() => {
    if (step !== "deal") return;
    const stage = stageRef.current;
    const pile = pileRef.current;
    const flyingLayer = flyingRef.current;
    if (!stage || !pile || !flyingLayer) return;

    const stageRect = stage.getBoundingClientRect();
    const pileX = pile.offsetLeft + pile.offsetWidth / 2;
    const pileY = pile.offsetTop + pile.offsetHeight / 2;

    let delay = 0;
    const perTile = 62;
    const flickTimers: number[] = [];
    seats.forEach((seat) => {
      const station = document.querySelector(`[data-seat="${seat.seatIndex}"]`);
      const dock = seat.isMe ? (document.querySelector(".dock-hand") as HTMLElement | null) : null;
      const targetEl = (station ?? dock) as HTMLElement | null;
      let tx = stageRect.width / 2;
      let ty = stageRect.height - 30;
      if (targetEl) {
        const r = targetEl.getBoundingClientRect();
        tx = r.left - stageRect.left + r.width / 2;
        ty = r.top - stageRect.top + r.height / 2;
      }
      for (let i = 0; i < 7; i++) {
        const tile = document.createElement("div");
        tile.className = "deal-fly";
        tile.style.left = `${pileX}px`;
        tile.style.top = `${pileY}px`;
        flyingLayer.appendChild(tile);
        const rot = (Math.random() - 0.5) * 160;
        const pan = Math.max(-0.8, Math.min(0.8, (tx / stageRect.width) * 2 - 1));
        flickTimers.push(window.setTimeout(() => sounds.dealFlick(pan), delay + 40));
        tile
          .animate(
            [
              { transform: "translate(-50%, -50%) rotate(0deg) scale(1)", opacity: 1, offset: 0 },
              {
                transform: `translate(calc(-50% + ${(tx - pileX) * 0.5}px), calc(-50% + ${
                  (ty - pileY) * 0.5 - 40
                }px)) rotate(${rot / 2}deg) scale(0.9)`,
                opacity: 1,
                offset: 0.55,
              },
              {
                transform: `translate(calc(-50% + ${tx - pileX}px), calc(-50% + ${ty - pileY}px)) rotate(${rot}deg) scale(0.55)`,
                opacity: 0,
                offset: 1,
              },
            ],
            { duration: 300, delay, easing: "cubic-bezier(0.3, 0.6, 0.4, 1)", fill: "both" },
          )
          .finished.then(
            () => tile.remove(),
            () => tile.remove(),
          );
        delay += perTile;
      }
    });

    const endT = setTimeout(finish, delay + 320);
    return () => {
      clearTimeout(endT);
      flickTimers.forEach((t) => clearTimeout(t));
      flyingLayer.replaceChildren();
    };
  }, [step]);

  return (
    <div
      className="deal-layer"
      ref={stageRef}
      onClick={finish}
      role="presentation"
      title="Skip"
    >
      <div className="deal-veil" />
      <div className="deal-banner">
        <span className="deal-round-ar">الدور {roundNumber}</span>
        <span className="deal-round-en">Round {roundNumber}</span>
      </div>

      <div className={`deal-pile ${step === "deal" ? "deal-pile-out" : ""}`} ref={pileRef}>
        <span className="deal-card deal-card-1" />
        <span className="deal-card deal-card-2" />
        <span className="deal-card deal-card-3" />
        <span className="deal-card deal-card-4" />
        <span className="deal-pile-label">{step === "shuffle" ? "الخلاطة…" : "توزيع…"}</span>
      </div>

      <div className="deal-fly-layer" ref={flyingRef} />

      <button type="button" className="deal-skip" onClick={finish}>
        تخطّي · skip
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hot-seat table chat — the same overlay as the online rooms, kept in memory
// for the session (no backend behind a pass-the-device game).
// ---------------------------------------------------------------------------

let localChatLog: Array<{ id: number; name: string; text: string }> = [];
let localChatSeq = 0;

export function LocalTableChat() {
  const [messages, setMessages] = useState(localChatLog);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [visible, setVisible] = useState(false);
  const hideTimer = useRef<number | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);
  const prevCount = useRef(localChatLog.length);

  useEffect(() => {
    if (messages.length <= prevCount.current) return;
    prevCount.current = messages.length;
    setVisible(true);
    endRef.current?.scrollIntoView({ block: "end" });
    if (hideTimer.current !== null) window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => setVisible(false), 7000);
  }, [messages]);

  useEffect(
    () => () => {
      if (hideTimer.current !== null) window.clearTimeout(hideTimer.current);
    },
    [],
  );

  // Enter opens the chat (only when no field or button has focus).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Enter") return;
      const el = document.activeElement as HTMLElement | null;
      if (
        el &&
        (el.tagName === "INPUT" ||
          el.tagName === "TEXTAREA" ||
          el.isContentEditable ||
          el.tagName === "BUTTON" ||
          el.getAttribute("role") === "button")
      ) {
        return;
      }
      e.preventDefault();
      setOpen(true);
      setVisible(true);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  function submit() {
    const text = draft.trim();
    if (!text) return;
    localChatSeq += 1;
    localChatLog = [...localChatLog, { id: localChatSeq, name: displayName() || "Table", text }].slice(-30);
    setMessages(localChatLog);
    setDraft("");
    setOpen(false);
  }

  const shown = messages.slice(-9);
  return (
    <div className={`lol-chat ${open ? "chat-open" : visible ? "chat-live" : "chat-idle"}`}>
      {(open || visible) && (
        <div className="lol-feed">
          {shown.length > 0 ? (
            shown.map((m) => (
              <div className="lol-line" key={m.id}>
                <span className="lol-name">({m.name}):</span> {m.text}
              </div>
            ))
          ) : (
            <div className="lol-line lol-muted">Table talk shows here 👋</div>
          )}
          <div ref={endRef} />
        </div>
      )}
      {open ? (
        <input
          className="lol-input"
          autoFocus
          placeholder="Say something…  (Enter to send · Esc to close)"
          value={draft}
          maxLength={240}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submit();
            }
            if (e.key === "Escape") {
              e.preventDefault();
              setOpen(false);
            }
          }}
          onBlur={() => setOpen(false)}
        />
      ) : (
        <button type="button" className="lol-chat-btn" onClick={() => { setOpen(true); setVisible(true); }}>
          💬 Chat
        </button>
      )}
    </div>
  );
}
